import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Heading, Spinner, Tag, Text, Image, Flex, VStack, HStack, Divider, PopoverTrigger, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody, Popover, PopoverContent, Icon, Stack, Button, SimpleGrid } from '@chakra-ui/react';
import { FaLinkedin, FaFacebook, FaInstagram } from 'react-icons/fa';
import { FaPen, FaClock } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { fetchCollaboratorDetailsApi } from '../api/api';
import ServiceCardSpecialist from './ServiceCardSpecialist';

function CollaboratorDetails() {
    const { t, i18n } = useTranslation();
    // const slug = "capucine-jallon";
    const { slug } = useParams();
    const [collaborator, setCollaborator] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // console.log("slug capucine-jallon", slug)
        const fetchCollaborator = async () => {
            setLoading(true);
            try {
                const data = await fetchCollaboratorDetailsApi(slug);
                // console.log("Collaborator data:", data);
                setCollaborator(data);  // Assuming setCollaborator updates the state with the fetched data
            } catch (error) {
                console.error('Error fetching collaborator details:', error);
            } finally {
                setLoading(false);  // Set loading to false once the request is done
            }
        };
        fetchCollaborator();
    }, [slug]);

    if (loading) {
        return (
            <Box textAlign="center" py="6" minH="85vh">
                <Spinner size="xl" color="#FF6666" thickness="4px" />
            </Box>
        );
    }

    if (!collaborator) {
        return (
            <Box textAlign="center" py="6">
                <Text fontSize="lg" color="gray.500">{t('specialist_not_found')}</Text>
            </Box>
        );
    }

    // Get the location object
    const location = collaborator.locations[0];

    // Dynamically select the correct fields based on language
    const locationName = location[`name_${i18n.language}`] || location.name;
    const locationAddress = location[`address_${i18n.language}`] || location.address;

    // Dynamically select the specialties and description
    const consultationSpecialties = collaborator.consultation_specialities.map(data => ({
        name: data[`name_${i18n.language}`] || data.name,
        description: data[`description_${i18n.language}`] || data.description
    }));

    return (
        <div>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>{`${collaborator.first_name} ${collaborator.last_name} - ${collaborator[`designation_${i18n.language}`] || collaborator.designation}`}</title>
                <meta
                    name="description"
                    content={`${t('learn_more_about')} ${collaborator.first_name} ${collaborator.last_name}, ${collaborator[`designation_${i18n.language}`] || collaborator.designation}. ${collaborator[`about_${i18n?.language?.split('_')[0]}`] || collaborator.about}`}
                />

                {/* Open Graph Tags */}
                <meta property="og:title" content={`${collaborator.first_name} ${collaborator.last_name} - ${collaborator[`designation_${i18n.language}`] || collaborator.designation}`} />
                <meta property="og:description" content={`${t('learn_more_about')} ${collaborator.first_name} ${collaborator.last_name}, ${collaborator[`designation_${i18n.language}`] || collaborator.designation}. ${collaborator[`about_${i18n?.language?.split('_')[0]}`] || collaborator.about}`} />
                <meta property="og:type" content="profile" />
                <meta property="og:url" content={window.location.href} />
                {collaborator?.image && <meta property="og:image" content={collaborator.image} />}

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${collaborator.first_name} ${collaborator.last_name} - ${collaborator[`designation_${i18n.language}`] || collaborator.designation}`} />
                <meta name="twitter:description" content={`${t('learn_more_about')} ${collaborator.first_name} ${collaborator.last_name}, ${collaborator[`designation_${i18n.language}`] || collaborator.designation}. ${collaborator[`about_${i18n?.language?.split('_')[0]}`] || collaborator.about}`} />
                {collaborator?.image && <meta name="twitter:image" content={collaborator.image} />}

                {/* Additional Professional Meta Tags */}
                {locationName && <meta name="geo.placename" content={locationName} />}
                {location?.zip_code && <meta name="geo.region" content={location.zip_code} />}
                <meta name="author" content={`${collaborator.first_name} ${collaborator.last_name}`} />
                {collaborator?.language_proficiency && (
                    <meta 
                        name="keywords" 
                        content={`${collaborator.first_name} ${collaborator.last_name}, ${collaborator[`designation_${i18n.language}`] || collaborator.designation}, ${collaborator.language_proficiency.map(lang => lang.name).join(', ')}, ${consultationSpecialties.map(spec => spec.name).join(', ')}`} 
                    />
                )}
            </Helmet>
            <Container maxW="6xl" py="8">
                {(collaborator?.image || collaborator?.consultation_specialities?.length > 0 || locationName) && (
                    <>
                        <Flex direction={{ base: 'column', md: 'row' }} alignItems="top" justifyContent="space-between" gap="2">
                            {collaborator?.image && (
                                <Image
                                    src={collaborator?.image}
                                    alt={collaborator?.slug || 'Collaborator'}
                                    boxSize="400px"
                                    objectFit="cover"
                                    borderRadius="30px"
                                />
                            )}

                            {(collaborator?.consultation_specialities?.length > 0 || locationName) && (
                                <VStack align="start" spacing="3" display={{ base: 'none', md: 'block' }}>
                                    <Box minW="350px" borderWidth="1px" borderRadius="lg" overflow="hidden" p={5}>
                                        <Text fontSize="xl" fontWeight="bold" textAlign="center" mb={3}>
                                            {t("at_a_glance")}
                                        </Text>

                                        {collaborator?.consultation_specialities?.[0]?.price_starting_from && (
                                            <Text fontSize="md" color="gray.600" mb={2}>
                                                {collaborator.consultation_specialities[0].price_starting_from} {t("per_session")}
                                            </Text>
                                        )}

                                        {locationName && (
                                            <Text color="gray.600" mb={1}>
                                                {locationName}, {locationAddress} {location?.zip_code}
                                            </Text>
                                        )}
                                    </Box>
                                </VStack>
                            )}
                        </Flex>
                        <Divider my="6" />
                    </>
                )}

                {(collaborator?.first_name || collaborator?.last_name || collaborator?.designation) && (
                    <>
                        <Box mb="6">
                            <Heading size="lg" textTransform="capitalize">
                                {collaborator?.first_name} {collaborator?.last_name}
                                {collaborator?.gender && (
                                    <Text as="span" fontSize="md" color="gray.500"> ({collaborator?.gender})</Text>
                                )}
                            </Heading>
                            <Text fontSize="lg" color="gray.600">
                                {collaborator[`designation_${i18n.language}`] || collaborator?.designation}
                            </Text>
                            {collaborator?.is_consultation_possible && (
                                <Tag
                                    size="lg"
                                    mt="2"
                                    px="4"
                                    py="2"
                                    bg="#dcf4ef"
                                    color="#255046"
                                    borderRadius="md"
                                    fontWeight="medium"
                                >
                                    {t('available_for_new_clients')}
                                </Tag>
                            )}
                        </Box>
                        <Divider my="6" />
                    </>
                )}

                {consultationSpecialties?.length > 0 && (
                    <>
                        <Box mb="6">
                            <Heading size="md" mb="4">{t('specialize_in')}</Heading>
                            <Flex wrap="wrap" gap="2">
                                <SimpleGrid
                                    columns={{ base: 1, md: 2, lg: 3 }}
                                    spacing={8}
                                    padding={0}
                                >
                                    {collaborator.consultation_specialities.map((data) => (
                                        // <Popover key={data.name} trigger="click">
                                        //     <PopoverTrigger>
                                        //         <Tag
                                        //             size="lg"
                                        //             variant="outline"
                                        //             borderRadius="full"
                                        //             px="4"
                                        //             py="2"
                                        //             fontSize="md"
                                        //             color="#FF6666"
                                        //             border="1px solid #FF6666"
                                        //             boxShadow="none"
                                        //             cursor="pointer"
                                        //             _hover={{
                                        //                 backgroundColor: "rgb(255 102 102 / 20%)",
                                        //             }}
                                        //         >
                                        //             {data.name}
                                        //         </Tag>
                                        //     </PopoverTrigger>
                                        //     <PopoverContent boxShadow="lg">
                                        //         <PopoverArrow />
                                        //         <PopoverCloseButton />
                                        //         <PopoverHeader fontWeight="bold">{data.name}</PopoverHeader>
                                        //         <PopoverBody>
                                        //             <Text>{data.description}</Text>
                                        //         </PopoverBody>
                                        //     </PopoverContent>
                                        // </Popover>

                                        <ServiceCardSpecialist
                                            key={data.id}
                                            service={data}
                                            specialistSlug={collaborator.slug}
                                        />

                                    ))}
                                </SimpleGrid>
                            </Flex>
                        </Box>
                        <Divider my="6" />
                    </>
                )}

                {(collaborator[`about_${i18n?.language?.split('_')[0]}`] || collaborator.about) && (
                    <>
                        <Box mb="6">
                            <Heading size="md" mb="4">{t('about_me')}</Heading>
                            <Text fontSize="md" color="gray.700">
                                {collaborator[`about_${i18n?.language?.split('_')[0]}`] || collaborator.about}
                            </Text>
                        </Box>
                        <Divider my="6" />
                    </>
                )}

                {collaborator?.language_proficiency && (
                    <>
                        <Box mb="6">
                            <Heading size="md" mb="4">{t('languages_i_speak')}</Heading>
                            <HStack spacing="2" wrap="wrap">
                                <Text>
                                    {collaborator?.language_proficiency
                                        ?.map((language) => language.name)
                                        .join(', ')}
                                </Text>
                            </HStack>
                        </Box>
                        <Divider my="6" />
                    </>
                )}

                {/* {(collaborator?.linkedin_url || collaborator?.facebook_url || collaborator?.instagram_url || collaborator?.email) && (
                    <>
                        <Box mb="6">
                            <Heading size="md" mb="4">{t('connect_with_me')}</Heading>
                            <HStack spacing="4">
                                {collaborator?.linkedin_url && (
                                    <a href={collaborator.linkedin_url} target="_blank" rel="noopener noreferrer">
                                        <Icon as={FaLinkedin} boxSize="6" color="#FF6666" _hover={{ color: "#e64e64" }} />
                                    </a>
                                )}
                                {collaborator?.facebook_url && (
                                    <a href={collaborator.facebook_url} target="_blank" rel="noopener noreferrer">
                                        <Icon as={FaFacebook} boxSize="6" color="#FF6666" _hover={{ color: "#e64e64" }} />
                                    </a>
                                )}
                                {collaborator?.instagram_url && (
                                    <a href={collaborator.instagram_url} target="_blank" rel="noopener noreferrer">
                                        <Icon as={FaInstagram} boxSize="6" color="#FF6666" _hover={{ color: "#e64e64" }} />
                                    </a>
                                )}
                                {collaborator?.email && (
                                    <a href={`mailto:${collaborator.email}`}>
                                        <Icon as={MdEmail} boxSize="6" color="#FF6666" _hover={{ color: "#e64e64" }} />
                                    </a>
                                )}
                            </HStack>
                        </Box>
                        <Divider my="6" />
                    </>
                )} */}

                {(locationName || locationAddress || location?.zip_code) && (
                    <Box>
                        <Heading size="md" mb="4">{t('location')}</Heading>
                        <Text fontSize="md" color="gray.700">
                            {locationName}{locationAddress ? `, ${locationAddress}` : ''}{location?.zip_code ? ` ${location.zip_code}` : ''}
                        </Text>
                    </Box>
                )}


                {(collaborator?.consultation_specialities?.length > 0 || locationName) && (
                    <VStack align="start" spacing="3" marginTop={10} display={{ base: 'block', md: 'none' }}>
                        <Box maxW="100%" minW="100%" borderWidth="1px" borderRadius="lg" overflow="hidden" p={5}>
                            <Text fontSize="xl" fontWeight="bold" textAlign="center" mb={3}>
                                {t("at_a_glance")}
                            </Text>

                            {collaborator?.consultation_specialities?.[0]?.price_starting_from && (
                                <Text fontSize="md" color="gray.600" mb={2}>
                                    {collaborator.consultation_specialities[0].price_starting_from} {t("per_session")}
                                </Text>
                            )}

                            {locationName && (
                                <Text color="gray.600" mb={1}>
                                    {locationName}, {locationAddress} {location?.zip_code}
                                </Text>
                            )}
                        </Box>
                    </VStack>
                )}

                {/* <Box className='booking-button' marginY={7}>
                    {collaborator?.slug && (
                        <Button
                            bg="#FF6666"
                            color="white"
                            width="100%"
                            height="50px"
                            fontSize="lg"
                            _hover={{
                                bg: '#fb4b4b'
                            }}
                            onClick={() => window.location.href = `../?specialist=${collaborator.slug}`}
                        >
                            {t("book_appointment")}
                        </Button>
                    )}

                </Box> */}
            </Container>
        </div >
    );
}

export default CollaboratorDetails;
